/**
 * Grid position validation and auto-correction utilities.
 *
 * This module provides functions to validate and automatically correct
 * grid item positions, ensuring a valid grid layout is always produced.
 */

import { castDraft, enableMapSet, produce } from 'immer';

// Enable Map/Set support for Immer
enableMapSet();

import { type Position, PositionType } from '#core/schema/defs.ts';

import type { GridLayout } from './schema.ts';

export type { GridLayout } from './schema.ts';

/**
 * Constraint for items that have a complete position.
 */
export interface HasPosition {
  position: Position;
}

/**
 * Constraint for items that may or may not have a position.
 */
export interface MaybeHasPosition {
  position?: Partial<Position>;
}

/**
 * Result of grid validation and correction.
 */
export interface GridValidationResult<ItemType> {
  layout: GridLayout;
  items: ItemType[];
  corrections: GridCorrection[];
}

/**
 * Describes a correction that was applied during validation.
 */
export interface GridCorrection {
  type: 'expand-rows' | 'expand-columns' | 'shift-overlap' | 'assign-position';
  itemIndex: number;
  original: Partial<Position> & Partial<GridLayout>;
  corrected: Partial<Position> & Partial<GridLayout>;
  message: string;
}

/**
 * Ensures an item has a complete position, filling in defaults for missing fields.
 * Uses PositionType's prefault defaults for all fields (row, column, rowSpan, columnSpan all default to 1).
 */
function ensurePosition(position: Partial<Position> | undefined): {
  position: Position;
  wasDefaulted: boolean;
} {
  const hasAllFields =
    position &&
    typeof position.row === 'number' &&
    typeof position.column === 'number' &&
    typeof position.rowSpan === 'number' &&
    typeof position.columnSpan === 'number';

  if (hasAllFields) {
    return { position: position as Position, wasDefaulted: false };
  }

  // Parse through PositionType to apply prefault defaults for all fields
  const filledPosition = PositionType.parse(position ?? {});
  return { position: filledPosition, wasDefaulted: true };
}

/**
 * Creates a cell key for overlap tracking.
 */
const cellKey = (row: number, column: number): string => `${row},${column}`;

/**
 * Gets all cells occupied by an item at the given position.
 */
const getOccupiedCells = (position: Position): string[] =>
  Array.from({ length: position.rowSpan }, (_, rOffset) =>
    Array.from({ length: position.columnSpan }, (_, cOffset) =>
      cellKey(position.row + rOffset, position.column + cOffset),
    ),
  ).flat();

/**
 * Finds the next available position for an item in the grid.
 * Searches row by row, column by column for a space that fits the item.
 * If no space found, extends the grid with a new row.
 */
function findNextAvailablePosition(
  position: Position,
  occupiedCells: Set<string>,
  layout: GridLayout,
): { position: Position; expandedLayout: GridLayout } {
  // Generate all candidate positions within the current grid
  const candidates = Array.from({ length: layout.rows }, (_, rowIndex) => {
    const r = rowIndex + 1;
    return Array.from({ length: layout.columns - position.columnSpan + 1 }, (_, colIndex) => {
      const c = colIndex + 1;
      return { row: r, column: c };
    });
  }).flat();

  // Find first position that fits without overlap
  const available = candidates.find(({ row, column }) => {
    const testPosition = { ...position, row, column };
    const testCells = getOccupiedCells(testPosition);
    const hasOverlap = testCells.some((cell) => occupiedCells.has(cell));
    const fitsInRows = row + position.rowSpan - 1 <= layout.rows;
    return !hasOverlap && fitsInRows;
  });

  if (available) {
    return {
      position: { ...position, row: available.row, column: available.column },
      expandedLayout: { ...layout },
    };
  }

  // No space found - extend grid with new row
  const newRow = layout.rows + 1;
  return {
    position: { ...position, row: newRow, column: 1 },
    expandedLayout: { ...layout, rows: newRow + position.rowSpan - 1 },
  };
}

/**
 * Internal accumulator state for grid validation reduce.
 */
interface ValidationAccumulator<ItemType> {
  layout: GridLayout;
  items: ItemType[];
  corrections: GridCorrection[];
  occupiedCells: Set<string>;
}

/**
 * Validates and corrects grid item positions.
 *
 * Handles four types of issues:
 * 1. **Missing position**: Item has no position → assign default (row 1, col 1, span 1x1)
 * 2. **Bounds exceeded**: Item's position + span exceeds grid dimensions → auto-expand grid
 * 3. **Overlap within grid**: Multiple items occupy same cells → shift later items to available space
 * 4. **No available space**: Grid is full → extend grid with new rows
 *
 * @param items - Grid items to validate (positions may be undefined or partial)
 * @param layout - Original grid layout dimensions
 * @returns Corrected items with guaranteed positions, plus list of corrections applied
 */
export function validateAndCorrectGrid<ItemType extends MaybeHasPosition>(
  items: ItemType[],
  layout: GridLayout,
): GridValidationResult<ItemType & HasPosition> {
  const initialState: ValidationAccumulator<ItemType & HasPosition> = {
    layout: { ...layout },
    items: [],
    corrections: [],
    occupiedCells: new Set<string>(),
  };

  const {
    layout: finalLayout,
    items: finalItems,
    corrections,
  } = items.reduce((acc, item, index) => {
    return produce(acc, (draft) => {
      // Ensure item has a complete position, defaulting missing fields
      const { position: ensuredPosition, wasDefaulted } = ensurePosition(item.position);
      const originalPosition = item.position ? { ...item.position } : {};
      const position = { ...ensuredPosition };

      if (wasDefaulted) {
        draft.corrections.push({
          type: 'assign-position',
          itemIndex: index,
          original: originalPosition,
          corrected: { ...position },
          message: `Item ${index} missing position. Assigned default: row ${position.row}, column ${position.column}, span ${position.rowSpan}x${position.columnSpan}.`,
        });
      }

      // Bounds checking - expand grid if item exceeds dimensions
      const requiredRows = position.row + position.rowSpan - 1;
      const requiredColumns = position.column + position.columnSpan - 1;

      if (requiredRows > draft.layout.rows) {
        draft.corrections.push({
          type: 'expand-rows',
          itemIndex: index,
          original: { rows: draft.layout.rows },
          corrected: { rows: requiredRows },
          message: `Item ${index} at row ${position.row} with rowSpan ${position.rowSpan} exceeds grid rows (${draft.layout.rows}). Expanding to ${requiredRows} rows.`,
        });
        draft.layout.rows = requiredRows;
      }

      if (requiredColumns > draft.layout.columns) {
        draft.corrections.push({
          type: 'expand-columns',
          itemIndex: index,
          original: { columns: draft.layout.columns },
          corrected: { columns: requiredColumns },
          message: `Item ${index} at column ${position.column} with columnSpan ${position.columnSpan} exceeds grid columns (${draft.layout.columns}). Expanding to ${requiredColumns} columns.`,
        });
        draft.layout.columns = requiredColumns;
      }

      // Overlap detection - shift items that collide with already-placed items
      const itemCells = getOccupiedCells(position);
      const hasOverlap = itemCells.some((cell) => draft.occupiedCells.has(cell));

      if (hasOverlap) {
        const { position: newPosition, expandedLayout } = findNextAvailablePosition(
          position,
          draft.occupiedCells,
          draft.layout,
        );

        draft.corrections.push({
          type: 'shift-overlap',
          itemIndex: index,
          original: { row: ensuredPosition.row, column: ensuredPosition.column },
          corrected: { row: newPosition.row, column: newPosition.column },
          message: `Item ${index} overlaps at (${ensuredPosition.row}, ${ensuredPosition.column}). Moving to (${newPosition.row}, ${newPosition.column}).`,
        });

        position.row = newPosition.row;
        position.column = newPosition.column;
        draft.layout.rows = expandedLayout.rows;
        draft.layout.columns = expandedLayout.columns;
      }

      // Track occupied cells for subsequent items
      const finalCells = getOccupiedCells(position);
      for (const cell of finalCells) {
        draft.occupiedCells.add(cell);
      }

      // Use castDraft to convert the new item to Draft type for Immer
      draft.items.push(castDraft({ ...item, position }));
    });
  }, initialState);

  return { layout: finalLayout, items: finalItems, corrections };
}

/**
 * Logs grid corrections as warnings.
 * Call this after validation to notify developers of auto-corrections.
 */
export function logGridCorrections(_corrections: GridCorrection[]): void {
  // corrections.forEach((correction) => {
  //   console.warn(`[DashboardGrid] ${correction.message}`);
  // });
}
